/**
 * Constants for Matches feature
 *
 * Configurable labels for event types, competitions, and sports.
 * These can be extended or overridden via API in the future.
 */

// Event types (not just football matches)
export const EVENT_TYPES = {
  match: { label: 'Match', color: '#68ff9a' },
  training: { label: 'Training', color: '#8ce5ff' },
  press_conference: { label: 'Press Conference', color: '#d4a5ff' },
  photoshoot: { label: 'Photoshoot', color: '#ffb366' },
  other: { label: 'Other Event', color: '#a0a0a0' },
};

// Sports types
export const SPORTS = {
  football: { label: 'Football', icon: null },
  tennis: { label: 'Tennis', icon: null },
  basketball: { label: 'Basketball', icon: null },
  hockey: { label: 'Hockey', icon: null },
  volleyball: { label: 'Volleyball', icon: null },
  athletics: { label: 'Athletics', icon: null },
  other: { label: 'Other', icon: null },
};

// Competitions live in the operator's Competition table (migration 0108);
// components read them through useCompetitions() and pass `bySlug` to the
// helpers below. A slug the table does not list (a MatchHub fixture name
// saved as free text) renders as stored, matching the server.

// Match status colors
export const MATCH_STATUS_COLORS = {
  upcoming: '#66b7ff',
  live: '#ff6b6b',
  past: '#888888',
  cancelled: '#ff6b6b',
};

// Assignment status
export const ASSIGNMENT_STATUS = {
  unassigned: { label: 'Unassigned', color: '#ff9b64' },
  partially_assigned: { label: 'Partial', color: '#ffeb7a' },
  fully_assigned: { label: 'Assigned', color: '#68ff9a' },
};

// Filter options for match list
export const MATCH_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'live', label: 'Live' },
  { key: 'past', label: 'Past' },
  { key: 'today', label: 'Today' },
  { key: 'no_photographer', label: 'No Photographer' },
  { key: 'no_editor', label: 'No Editor' },
];

// Day names for calendar
export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const WEEKDAYS_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// Month names
export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Colour for an event: its competition's colour when the table lists it,
 * else the event type's.
 * @param {Object} bySlug - slug -> competition row, from useCompetitions()
 */
export function getEventColor(bySlug, event) {
  const comp = event?.competition ? bySlug?.[event.competition] : null;
  if (comp?.color) return comp.color;
  if (event?.event_type && EVENT_TYPES[event.event_type]) {
    return EVENT_TYPES[event.event_type].color;
  }
  return EVENT_TYPES.match.color;
}

/** Label for a competition slug; the slug itself when the table does not list it. */
export function getCompetitionLabel(bySlug, competitionKey) {
  return bySlug?.[competitionKey]?.label || competitionKey || 'Unknown';
}

/** Title short code for a competition slug, '' when unlisted. */
export function getCompetitionShortCode(bySlug, competitionKey) {
  return bySlug?.[competitionKey]?.short_code || '';
}

// Provider (MatchHub/Sportmonks) competition names that differ from our
// labels. Lowercased provider name -> slug. An exact (case-insensitive) label
// match is tried first, so only the names that differ need an entry here.
const COMPETITION_ALIASES = {
  'champions league': 'champions_league',
  'europa league': 'europa_league',
  'conference league': 'conference_league',
  'uefa europa conference league': 'conference_league',
  'fifa world cup': 'world_cup',
  'european championship': 'euros',
  'euro championship': 'euros',
  euros: 'euros',
  'nations league': 'nations_league',
  'youth league': 'uefa_youth_league',
  'vrouwen eredivisie': 'eredivisie_vrouwen',
  'club friendlies': 'club_friendly',
  'friendlies international': 'international_friendly',
  friendlies: 'international_friendly',
  'premier league': 'premier_league',
  championship: 'championship',
  bundesliga: 'bundesliga',
  '2. bundesliga': 'bundesliga_2',
  'la liga': 'la_liga',
  laliga: 'la_liga',
  'la liga 2': 'segunda_division',
  'segunda división': 'segunda_division',
  'ligue 1': 'ligue_1',
  'ligue 2': 'ligue_2',
  'primeira liga': 'primeira_liga',
  'liga portugal': 'primeira_liga',
  'serie a': 'serie_a',
  'serie b': 'serie_b',
  'pro league': 'pro_league',
  'jupiler pro league': 'pro_league',
  'first division a': 'pro_league',
  'challenger pro league': 'challenger_pro_league',
};

/**
 * Resolve a provider competition NAME (e.g. "Eredivisie", "UEFA Champions
 * League") to a slug in the Competition table, or null when unknown. The
 * caller keeps the raw name in that case (Match.competition is free-text) and
 * injects it as a selectable option so it still displays.
 * @param {Array} competitions - rows from useCompetitions()
 */
export function resolveCompetitionSlug(competitions, apiName) {
  if (!apiName) return null;
  const norm = String(apiName).trim().toLowerCase();
  if (!norm) return null;
  const rows = competitions || [];
  const exact = rows.find((c) => c.label.toLowerCase() === norm || c.slug === norm);
  if (exact) return exact.slug;
  const alias = COMPETITION_ALIASES[norm];
  if (alias && rows.some((c) => c.slug === alias)) return alias;
  return null;
}

/**
 * The default match name, 'Home - Away Competition Season'. Mirrors the
 * server's Match.derive_name() so the wizard shows the string it will save;
 * the operator may overwrite it, and the field is required either way.
 */
export function deriveMatchName({ eventType, homeName, awayName, competitionLabel, season, startTime }) {
  if ((eventType || 'match') === 'match' && homeName && awayName) {
    return [`${homeName} - ${awayName}`, competitionLabel, season].filter(Boolean).join(' ').trim();
  }
  const label = String(eventType || 'event')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
  const d = startTime ? new Date(startTime) : null;
  const when =
    d && !Number.isNaN(d.getTime())
      ? `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`
      : '';
  return [label, homeName || awayName, when].filter(Boolean).join(' ').trim();
}

// Get label for event type
export function getEventTypeLabel(typeKey) {
  return EVENT_TYPES[typeKey]?.label || typeKey || 'Event';
}

/**
 * Derive a football-season label like "2025/2026" from a Date.
 *
 * Convention: matches in Jan–Jul (month index 0–6) belong to the season
 * that started the previous calendar year; Aug–Dec (7–11) start a new
 * season. Mirrors the server-side derive_season() helper so Tank's
 * preview matches the final export.
 */
export function computeSeason(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const startYear = d.getMonth() < 7 ? year - 1 : year;
  return `${startYear}/${startYear + 1}`;
}

/**
 * Format a season label "YYYY/YYYY+1" from a starting year. MatchHub's
 * (Sportmonks) season.year is the STARTING year — 2024 ⇒ "2024/2025" — so a
 * fixture prefill matches the dropdown's option format. Returns "" if not a
 * finite year.
 */
export function formatSeasonLabel(startYear) {
  const y = Number(startYear);
  // Reject NaN and coercions like Number(null)===0 / Number('')===0; only a
  // plausible four-digit year formats.
  if (!Number.isInteger(y) || y < 1900 || y > 2100) return '';
  return `${y}/${y + 1}`;
}

/**
 * Generate season dropdown options. Returns an array of `{ value, label }`
 * covering current year ± 2, plus the "" (auto) sentinel that tells the
 * server to derive from start_time.
 */
export function seasonOptions(now = new Date()) {
  const current = computeSeason(now);
  const year = now.getFullYear();
  const baseYear = now.getMonth() < 7 ? year - 1 : year;
  const seasons = [];
  for (let offset = -2; offset <= 2; offset += 1) {
    const start = baseYear + offset;
    seasons.push(`${start}/${start + 1}`);
  }
  return [
    { value: '', label: `Auto (${current})` },
    ...seasons.map((s) => ({ value: s, label: s })),
  ];
}
